import React, { useCallback, useMemo, useState } from 'react'
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Stack,
  Button,
  Alert,
  Collapse,
  IconButton,
  AlertTitle,
  Tooltip,
  CircularProgress,
} from '@mui/material'
import DatePicker from 'components/DatePicker'
import { useNavigate } from 'react-router-dom'
import { useConversionReport } from 'hooks/useConversionReport'
import ConversionReportPageSkeleton from 'components/Skeletons/ConversionReportPage'
import useAdInsightsReport from 'queries/use-ad-insights-report'
import MetaAds from 'components/svg/MetaAds'
import useAdsAccounts from 'queries/useAdAccounts'
import { Close, RefreshRounded } from '@mui/icons-material'
import { initialState as initialStateConversionReport } from 'contexts/conversion-report/reducer'
import AccountConnectSVG from '../svg/AccountConnectSVG'
import ConversionReportSVG from '../svg/ConversionReportSVG'
import AccountConnectedSelect from './AccountConnectedSelect'
import ConversionReportCards from './Cards'
import FilterDrawer from './FilterDrawer'
import ActionButtons from './ActionButtons'
import DataGridReport from './DataGridReport'
import { PRESET_PERIODS } from './constants'

function TabPanel(props) {
  const { children, value, index, ...other } = props

  return (
    <div role="tabpanel" hidden={value !== index} id={`tabpanel-${index}`} aria-labelledby={`tab-${index}`} {...other}>
      {value === index && <Box sx={{ p: '1.5rem 0.5rem' }}>{children}</Box>}
    </div>
  )
}

function openTab(index) {
  return {
    id: `tab-${index}`,
    'aria-controls': `tabpanel-${index}`,
  }
}

export default function ConversionReportTabs() {
  const navigate = useNavigate()
  const [value, setValue] = useState(0)

  const [showDrawer, setShowDrawer] = useState(false)
  const [showAlert, setShowAlert] = useState({ connect: true, divergency: true })

  const {
    state: { campaigns, ads, adAccountId, date, search, statuses, page },
    actions: { setDate, setPagination },
  } = useConversionReport()
  const [dates, setDates] = useState(() => [date.start, date.end])
  const { data, isFetching, refetch } = useAdInsightsReport({
    accountId: adAccountId,
    startDate: dates[0],
    endDate: dates[1],
    groupBy: search,
    campaigns,
    ads,
    statuses,
    page: page.current + 1,
    perPage: page.size,
  })
  const { data: accounts } = useAdsAccounts()
  const selectedAccount = accounts?.find((acc) => acc.adAccountId === adAccountId)

  const tabTitle = useMemo(() => {
    if (value === 0) return 'Relatório de eventos e conversões'

    return ''
  }, [value])

  const handleChange = useCallback(
    (event, newValue) => {
      setValue(newValue)
    },
    [setValue],
  )

  const handleDateChange = useCallback(
    ([startDate, endDate]) => {
      setDates([startDate, endDate])
      setDate({ start: startDate, end: endDate })
      setPagination({
        current: initialStateConversionReport.page.current,
        size: initialStateConversionReport.page.size,
      })
    },
    [adAccountId],
  )

  const toggleDrawer = useCallback(() => {
    setShowDrawer(!showDrawer)
  }, [showDrawer])

  const content = useMemo(() => {
    let toRender = null
    if (isFetching) {
      return (
        <Box mt={4} borderTop={1} borderColor="divider">
          <ConversionReportPageSkeleton />
        </Box>
      )
    }
    if (!adAccountId) {
      if (!accounts?.length) {
        toRender = (
          <Stack alignItems="center" justifyContent="center" gap={2}>
            {showAlert.connect && (
              <Alert
                severity="info"
                sx={{ marginTop: '32px' }}
                action={
                  <IconButton size="small" onClick={() => setShowAlert({ ...showAlert, connect: false })}>
                    <Close />
                  </IconButton>
                }
              >
                <b>Importante:</b> Apenas campanhas com veiculação ‘ativa’ e do tipo CTWA serão importadas na conexão de
                contas. Os dados anteriores à sua conexão de contas com Meta Ads só poderão ser consultados no filtro
                ‘Período máximo’.
              </Alert>
            )}

            <AccountConnectSVG width="20rem" height="20rem" />
            <Typography variant="body2" color="text.secondary" maxWidth="40rem" textAlign="center">
              Sem contas de anúncios Meta Ads conectadas.
              <br />
              <br />
              Conecte sua conta Meta Ads e simplifique o acompanhamento das métricas de anúncios Click to WhatsApp em um
              único lugar. ir para conexão de contas
            </Typography>
            <Button
              variant="contained"
              sx={{
                bgcolor: '#00AEEF',
                ':hover': { bgcolor: '#009deb' },
                borderRadius: '2rem',
                padding: '0.8rem 1rem',
              }}
              data-testid="test-button-connect-account"
              onClick={() => {
                navigate(`/accountsSelfConnection`, { replace: true })
              }}
            >
              IR PARA CONEXÃO DE CONTAS
            </Button>
          </Stack>
        )
      } else {
        toRender = (
          <>
            <Collapse in={showAlert.divergency}>
              <Alert
                severity="info"
                onClose={() => setShowAlert({ ...showAlert, divergency: false })}
                sx={{ marginTop: '32px' }}
              >
                As informações apresentadas neste relatório estão sujeitas a pequenas divergências devido a possíveis
                variações no processamento dos dados.
              </Alert>
            </Collapse>
            <Stack alignItems="center" justifyContent="center" gap={2}>
              <ConversionReportSVG width="20rem" height="20rem" />
              <Typography variant="body2" color="text.secondary" maxWidth="40rem" textAlign="center">
                <strong>Selecione a contas de anúncios Meta Ads que deseja visualizar.</strong>
                <br />
                <br />
                Com suas contas de anúncios Meta Ads já conectadas, é o momento de escolher a conta que deseja
                visualizar os dados. Utilize o botão abaixo para selecionar a conta.
              </Typography>

              <AccountConnectedSelect variant="contained" />
            </Stack>
          </>
        )
      }
    }

    return toRender
  }, [accounts?.length, isFetching, navigate, adAccountId, showAlert])

  return (
    <Box>
      {showDrawer && <FilterDrawer open={showDrawer} toggleDrawer={toggleDrawer} />}
      <Typography
        sx={{
          fontSize: '2rem',
          fontWeight: '900',
          lineHeight: 1.5,
        }}
      >
        {tabTitle}
      </Typography>
      <Typography variant="body2" sx={{ mb: '2rem' }}>
        Obtenha insights detalhados sobre suas campanhas de cliques para WhatsApp (CTWA) nos canais conectados à Hi
        Platform. Acompanhe os resultados dos seus anúncios de Ads e combine-os com eventos e métricas geradas após o
        início da conversa.
      </Typography>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={value}
          onChange={handleChange}
          sx={{
            '& .HiTheme-tab-selected': {
              color: '#00AEEF !important',
            },
            '& .HiTheme-Tabs-indicator': {
              backgroundColor: '#00AEEF',
            },
          }}
          aria-label="tab click to whatsapp report"
        >
          <Tab
            label="Meta ads - CTWA"
            icon={<MetaAds />}
            iconPosition="start"
            {...openTab(0)}
            classes={{
              selected: 'HiTheme-tab-selected',
            }}
          />

          {/* <Tab
            label="TikTok - CTWA"
            icon={<TikTok />}
            iconPosition="start"
            classes={{ selected: 'HiTheme-tab-selected' }}
            disabled
          /> */}
        </Tabs>
      </Box>
      <TabPanel value={value} index={0}>
        <Stack direction="row" gap={2} alignItems="center" borderBottom={1} borderColor="divider" pb="2rem">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              Desempenho de anúncios - Click To WhatsApp
            </Typography>
            <Typography variant="body2" maxWidth="70%">
              Veja as métricas de desempenho dos seus anúncios cliques para WhatsApp (CTWA) - Meta Ads. Monitore
              conversas iniciadas e o custo por conversa em um único relatório fácil de visualizar.
            </Typography>
          </Box>

          <Stack
            flexDirection="row"
            gap={2}
            alignItems="center"
            justifyContent="flex-end"
            component="section"
            ml="auto"
            data-testid="datepicker-section"
          >
            <AccountConnectedSelect />
            <DatePicker
              onChange={handleDateChange}
              value={dates}
              presetPeriods={PRESET_PERIODS}
              disabled={!adAccountId}
            />
            <Tooltip title="Atualizar dados do relatório" placement="top">
              <IconButton
                role="progressbar"
                onClick={() => {
                  if (!isFetching) refetch()
                }}
                size="medium"
                sx={{
                  color: '#00AEEF',
                }}
                disabled={!adAccountId}
              >
                {isFetching ? (
                  <CircularProgress
                    size={23}
                    sx={{
                      color: '#00AEEF',
                    }}
                  />
                ) : (
                  <RefreshRounded />
                )}
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
        {content}
        {adAccountId && !isFetching ? (
          <Stack flexDirection="column" gap={2}>
            {selectedAccount?.invalidatedToken && (
              <Alert
                severity="error"
                sx={{ marginTop: '32px', alignItems: 'center' }}
                action={
                  <Button
                    variant="text"
                    color="error"
                    onClick={() => navigate(`/accountsSelfConnection`, { replace: true })}
                  >
                    conectar conta
                  </Button>
                }
              >
                <AlertTitle>Conta desconectada</AlertTitle>
                Pode haver divergências nos dados. Os dados não serão capturados até a reconexão de conta.
                <br />
                <b>Recomendamos que reconecte sua conta o mais rápido possível.</b>
              </Alert>
            )}

            {data?.Results && <ConversionReportCards reportData={data} />}
            <ActionButtons toggleDrawer={toggleDrawer} />
            <DataGridReport reportData={data?.Results} isFetching={isFetching} totalRows={data?.TotalResults} />
          </Stack>
        ) : null}
      </TabPanel>
      <TabPanel value={value} index={1}>
        <Box>
          <Typography>Texto teste</Typography>
        </Box>
      </TabPanel>
    </Box>
  )
}
